import type { RestaurantEvent } from '@shared/types/restaurants';
import { RestaurantEventCard } from './RestaurantEventCard';
import { Divider } from '@shared/components/divider/Divider';
import styles from './RestaurantEventCardPreview.scss';
import { Icon } from '@components/icon/Icon';
import SkeletonPlaceholder from '@shared/components/skeletonPlaceholder/SkeletonPlaceholder';
import { formatPercent } from '@shared/utils/formatPercent';
import { centsToDollar } from '@shared/utils/currency';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import cx from 'classnames';
import typography from '~styles/typography.scss';

export type RestaurantEventPreviewOverrides = Partial<RestaurantEvent>;

function hasDisclaimersToShowInPreview(event: RestaurantEvent | undefined) {
  if (!event?.name?.trim()) {
    return false;
  }
  return (
    event.disclaimers?.some(
      (disclaimer) => String(disclaimer.title ?? '').trim() !== '',
    ) ?? false
  );
}

export const RestaurantEventCardPreview = ({
  eventOverrides,
  fetchedEvent,
}: {
  eventOverrides?: RestaurantEventPreviewOverrides;
  fetchedEvent: RestaurantEvent | null;
}) => {
  const event =
    fetchedEvent != null || eventOverrides != null
      ? ({ ...fetchedEvent, ...eventOverrides } as RestaurantEvent)
      : undefined;

  const showDisclaimersContent = hasDisclaimersToShowInPreview(event);
  const serviceFeesToPreview =
    event?.serviceFees && event.serviceFees.length > 0 && event.name !== ''
      ? event.serviceFees
      : null;

  return (
    <RestaurantEventCard restaurantEvent={event} isPreview>
      {event?.description ? (
        <section className={styles.restaurantEventDescription}>
          <div className={styles.descriptionIcon}>
            <Icon name="file" />
          </div>
          <div className={styles.descriptionText}>
            <p className={styles.sectionTitle}>Description</p>
            <p>{event?.description}</p>
          </div>
        </section>
      ) : (
        <section
          className={styles.descriptionSkeleton}
          style={{ marginBottom: '1rem' }}
        >
          {SkeletonPlaceholder({ width: 32, height: 32 })}
          <div className={styles.descriptionText}>
            {SkeletonPlaceholder({ width: 100, height: 16 })}
            {SkeletonPlaceholder({ width: 311, height: 16 })}
          </div>
        </section>
      )}

      {serviceFeesToPreview ? (
        <section className={styles.serviceCharges}>
          {serviceFeesToPreview.map((fee) => (
            <div key={fee.id} className={styles.serviceChargeItem}>
              <div className={styles.sectionTitle}>
                {fee.label || 'Service fee'}
              </div>
              <div className={styles.serviceChargeItemValue}>
                {formatPercent(fee.percentage)}
              </div>
            </div>
          ))}
        </section>
      ) : (
        <section className={styles.serviceCharges}>
          <SkeletonPlaceholder
            width={100}
            height={16}
            baseColor="var(--greyEventCard)"
          />
          <SkeletonPlaceholder
            width={50}
            height={16}
            baseColor="var(--greyEventCard)"
          />
        </section>
      )}

      {/* Add-ons */}
      <section className={styles.addOns}>
        {(event?.addOns?.length ?? 0) > 0 ? (
          <>
            <Divider className={styles.divider} />
            <div className={styles.addOnsList}>
              <div className={styles.sectionTitle}>Add-ons</div>
              {(event?.addOns ?? []).map((addOn) => (
                <div key={addOn.id} className={styles.addOnsItem}>
                  <div className={styles.addOnsItemImage}>
                    <img
                      src={
                        addOn.imageKey
                          ? getRestaurantImagePath(addOn.imageKey)
                          : undefined
                      }
                      alt={addOn.name}
                    />
                  </div>
                  <div className={styles.addOnsItemContent}>
                    <div className={styles.addOnsItemDetails}>
                      <div className={styles.addOnsItemName}>{addOn.name}</div>
                      <div className={styles.addOnsItemPrice}>
                        {centsToDollar(addOn.price)}
                      </div>
                    </div>
                    <div className={styles.addOnsItemTiers}>
                      {(addOn.tiers?.length ?? 0) > 0 && (
                        <span className={cx(typography.t2, styles.tierBadge)}>
                          <Icon name="ticket" />
                          {(addOn.tiers ?? [])
                            .map((tier) => tier.name)
                            .join(', ')}
                        </span>
                      )}
                    </div>
                    {addOn.description && (
                      <div className={styles.addOnsItemDescription}>
                        {addOn.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </section>

      <Divider className={styles.divider} />
      <section className={styles.disclaimers}>
        {showDisclaimersContent ? (
          <div className={styles.disclaimerIconContainer}>
            <Icon name="infoCircle" className={styles.disclaimerIcon} />
          </div>
        ) : (
          <SkeletonPlaceholder width={32} height={32} />
        )}
        <div className={styles.disclaimerContent}>
          <div className={styles.sectionTitle}>
            {showDisclaimersContent
              ? 'Disclaimers'
              : SkeletonPlaceholder({ width: 100, height: 16 })}
          </div>
          <div className={styles.disclaimerItems}>
            {showDisclaimersContent ? (
              event?.disclaimers
                ?.filter(
                  (disclaimer) => String(disclaimer.title ?? '').trim() !== '',
                )
                .map((disclaimer) => (
                  <div key={disclaimer.id} className={styles.disclaimerItem}>
                    {disclaimer.title}{' '}
                    {disclaimer.description ? (
                      <span className={styles.disclaimerDescription}>
                        : {disclaimer.description}
                      </span>
                    ) : null}
                  </div>
                ))
            ) : (
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}
              >
                <SkeletonPlaceholder width={50} height={16} />
                <SkeletonPlaceholder width={100} height={16} />
              </div>
            )}
          </div>
        </div>
      </section>

      <Divider className={styles.divider} />
      {/* Guest Capacity */}
      <section className={styles.tiers}>
        <div className={styles.tiersContent}>
          {(event?.tiers?.length ?? 0) > 0 ? (
            event?.tiers.map((tier) => (
              <div key={tier.id} className={styles.tiersItemContainer}>
                <div className={styles.tiersItemInfo}>
                  {(tier.price || 0) > 0 ? (
                    <div className={styles.tiersIcon}>
                      <Icon name="ticket" className={styles.ticketIcon} />
                    </div>
                  ) : (
                    <SkeletonPlaceholder width={32} height={32} />
                  )}
                  <div className={styles.tiersItem}>
                    {(tier.price || 0) > 0 ? (
                      <div className={styles.tiersItemName}>{tier.name}</div>
                    ) : (
                      <SkeletonPlaceholder width={150} height={16} />
                    )}
                    <div className={styles.tiersItemTableCount}>
                      {(tier.price || 0) > 0 ? (
                        `Table count: ${tier.floorPlanTableIds.length}`
                      ) : (
                        <SkeletonPlaceholder width={100} height={16} />
                      )}
                    </div>
                  </div>
                  <div className={styles.tiersItemPrice}>
                    {(tier.price || 0) > 0 ? (
                      `${centsToDollar(tier.price)}`
                    ) : (
                      <SkeletonPlaceholder width={50} height={16} />
                    )}
                  </div>
                </div>
                <div className={styles.tiersItemCapacity}>
                  {(tier.capacity || 0) > 0 ? (
                    `${tier.capacity}`
                  ) : (
                    <SkeletonPlaceholder width={50} height={16} />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className={styles.tiersItemContainer}>
              <div className={styles.tiersIcon}>
                <SkeletonPlaceholder width={32} height={32} />
              </div>
              <div className={styles.tiersItemInfo}>
                <div className={styles.tiersItem}>
                  <div className={styles.tiersItemName}>
                    <SkeletonPlaceholder width={200} height={16} />
                  </div>
                  <div className={styles.tiersItemTableCount}>
                    <SkeletonPlaceholder width={100} height={16} />
                  </div>
                </div>
                <div className={styles.tiersItemPrice}>
                  <SkeletonPlaceholder width={50} height={16} />
                </div>
              </div>
              <div className={styles.tiersItemCapacity}>
                <SkeletonPlaceholder width={50} height={16} />
              </div>
            </div>
          )}
        </div>
      </section>
      <section className={styles.guestInformationTotal}>
        <div>
          {event?.capacity
            ? 'Total Capacity'
            : SkeletonPlaceholder({
                width: 100,
                height: 16,
                baseColor: 'var(--greyEventCard)',
              })}
        </div>
        {event?.capacity ? (
          <div className={styles.guestInformationTotalValue}>
            {event?.capacity} guests
          </div>
        ) : (
          <SkeletonPlaceholder
            width={100}
            height={16}
            baseColor="var(--greyEventCard)"
          />
        )}
      </section>
    </RestaurantEventCard>
  );
};
